/**
 * DataLoader - a generic component to render children based on loading, error or success  state of a GraphQL query or mutation.
 */

import { styled } from "@mui/material";
import { Children, isValidElement, ReactElement, ReactNode } from "react";
import { GqlError } from "../common/GqlError";
import { LoadingIcon } from "../ui/LoadingIcon";

interface DefaultProps {
  small: boolean;
}

interface ContainerNodes {
  LoadingContainer?: ReactNode;
  ErrorContainer?: ReactNode;
  SuccessContainer?: ReactNode;
  EmptyContainer?: ReactNode;
}

interface DataLoaderProps {
  loading: boolean;
  error?: object | null | Error;
  success: boolean;
  children: ReactElement<ContainerNodes> | ReactElement<ContainerNodes>[];
  defaults?: DefaultProps;
}

interface StateContainerProps {
  children: ReactNode;
}

export const LoadingContainer = ({ children }: StateContainerProps) => <>{children}</>;

export const ErrorContainer = ({ children }: StateContainerProps) => <>{children}</>;

export const SuccessContainer = ({ children }: StateContainerProps) => <>{children}</>;

export const EmptyContainer = ({ children }: StateContainerProps) => <>{children}</>;

const determineStateContainerType = (loading: boolean, error: object | null | Error | undefined, success: boolean) => {
  if (loading) return LoadingContainer;
  else if (error) return ErrorContainer;
  else if (success) return SuccessContainer;
  else return EmptyContainer;
};

interface CardProps {
  spacer?: boolean;
}

const Card = styled("div")<CardProps>`
  padding-top: ${(props) => (props.spacer === true ? "51px" : "initial")};
`;

const DefaultLoading = () => <LoadingIcon />;
const DefaultError = ({ error, small }: { error?: DataLoaderProps["error"]; small?: boolean }) => {
  return (
    <Card>
      <GqlError error={error instanceof Error ? error : undefined} small={small} />
    </Card>
  );
};
const DefaultNoResults = () => <Card>No Items</Card>;

const findStateContainer = ({
  children,
  stateContainerType,
}: {
  children: DataLoaderProps["children"];
  stateContainerType: ReturnType<typeof determineStateContainerType>;
}) => {
  let stateContainer: ReactNode = null;
  Children.forEach(children, (child) => {
    if (isValidElement(child) && child.type === stateContainerType) stateContainer = child;
  });

  return stateContainer;
};

/**
 * @deprecated This component is deprecated and will be removed in future releases. Native datagrid processing or QueryResult shall be used instead.
 */
export const DataLoader = ({ loading, error, success, children, defaults }: DataLoaderProps) => {
  const stateContainerType = determineStateContainerType(loading, error, success);

  const stateContainer = findStateContainer({ children, stateContainerType });
  if (stateContainerType === LoadingContainer) {
    return stateContainer ? <>{stateContainer}</> : <DefaultLoading />;
  } else if (stateContainerType === ErrorContainer) {
    return stateContainer ? <>{stateContainer}</> : <DefaultError error={error} small={defaults?.small} />;
  } else if (stateContainerType === EmptyContainer) {
    return stateContainer ? <>{stateContainer}</> : <DefaultNoResults />;
  }
  return <>{stateContainer}</>;
};
